import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "react-query";
import CustomerApiService from "../../../CustomerApiService";
import { tabForJob } from "./jobBucketing";
import {
  filmsGroundFixturesEnabled,
  listFixtureJobSummaries,
} from "../filmsGround/__fixtures__/anchorJobFixtures";

/**
 * Spotter Control Room tab API calls (Monday 12911625908) — the read side of the status-push
 * pipeline `apps/customer-api/src/routes/spotter/spotterJobStatus.ts` feeds on every n8n push
 * (Monday 12908478970, PR #38). Mirrors `../usage/service.ts`'s convention: a thin wrapper over
 * `CustomerApiService.fetch`, bare-JSON contract, a named `useQuery` hook with polling.
 *
 * Type mirrors `apps/customer-api/src/services/spotterControlRoom.ts`'s `ControlRoomJob` by hand
 * (the two apps don't share a types package — see `apps/customer-frontend/CLAUDE.md`'s "Data
 * fetching" section). Deliberately has NO `counts` field: per the customer's own decision on this
 * item, `counts` stays stage-name-only with no numbers, and the backend never sends it — nothing
 * here to build against.
 *
 * `useSetControlRoomJobDismissed` (Monday 12921148830) is the one mutation this file exports —
 * `ControlRoomScreen.tsx`'s tab filter/dismiss-toggle UI is the only caller.
 *
 * FIXTURES (Addison: "can you just make the fixture show in the sidebar so I don't have to go
 * through this?" — clicking a pre-authed link straight to `/spotter/control-room/:jobId`):
 * `getControlRoomJobs` returns `buildFixtureControlRoomJobs()` instead of calling the real API
 * when `filmsGroundFixturesEnabled()` (`../filmsGround/__fixtures__/anchorJobFixtures.ts`) is set,
 * same branching shape `../filmsGround/service.ts`'s own `getAnchorJobs` already uses. Built off
 * `listFixtureJobSummaries()` — the SAME fixture list `useAnchorJobs()` reads for each rail row's
 * `ReviewStatusBadge` — so a fixture row's badge and its rail bucket (`hasReview`/
 * `reviewNeedsInput` here) can never disagree about the same job. The hide/unhide mutation below
 * is NOT fixture-aware (still calls the real `PUT .../dismiss`) — hiding a fixture row is a rare
 * enough action in a dev-only mode that it wasn't worth a fixture branch for a mutation with no
 * fixture-side state to mutate.
 */

export type SpotterJobMode =
  | "game"
  | "sideline"
  | "broadcast"
  | "films_ground"
  | "top_films"
  | "practice"
  | "intersquad"
  | "wire"
  | "general"
  | "subclips"
  | "unknown";

export type SpotterJobMainStage =
  | "queued"
  | "processing_footage"
  | "identifying_plays"
  | "tagging"
  | "creating"
  | "cutting"
  | "scanning"
  | "done";

/** `warning` is a finished run where some items failed; the job's `note` says which. */
export type SpotterJobStatus =
  | "in_progress"
  | "blocked"
  | "complete"
  | "error"
  | "warning";

const PIPELINE_STAGES: SpotterJobMainStage[] = [
  "queued",
  "processing_footage",
  "identifying_plays",
  "tagging",
  "done",
];

const SUBCLIPS_STAGES: SpotterJobMainStage[] = [
  "queued",
  "creating",
  "cutting",
  "scanning",
  "done",
];

/** The stepper's stages for a job. A job whose mode hasn't resolved yet still follows the
 *  subclips list once it reports a subclips-only stage. */
export function stagesForJob(
  job: Pick<ControlRoomJob, "mode" | "latestStage">
): SpotterJobMainStage[] {
  const isSubclips =
    job.mode === "subclips" ||
    (job.mode === "unknown" &&
      !!job.latestStage &&
      !PIPELINE_STAGES.includes(job.latestStage));
  return isSubclips ? SUBCLIPS_STAGES : PIPELINE_STAGES;
}

export type SpotterJobTargetKind = "collection" | "saved_search" | "assets";

/** What the run was triggered against (Monday 12871494046, Seth: "add the collection or saved
 *  search name next to the job id"). `undefined` on `ControlRoomJob` until n8n starts sending it
 *  — see the backend's `spotterControlRoom.ts` doc comment for the resolution/caching story
 *  (`name`/`url` are ALREADY resolved server-side by the time this reaches the browser; the
 *  frontend never calls iconik itself). */
export interface ControlRoomJobTarget {
  kind: SpotterJobTargetKind;
  /** `collection`/`saved_search` only. */
  name?: string;
  /** `collection` only — the immediate parent collection's title, server-resolved (Monday
   *  12937644849). `undefined` for a root-level collection, a non-collection target, or on
   *  resolution failure. */
  parentName?: string;
  /** `collection`/`saved_search` only — deep link into iconik's own web UI. */
  url?: string;
  /** `assets` only. */
  assetCount?: number;
}

/** The five game/practice modes' row label (Monday 12937644849) — mirrors the backend's
 *  `SpotterGameLabel`. */
export interface ControlRoomGameLabel {
  primary: string;
  secondary?: string;
}

export interface ControlRoomJobReportLink {
  label: string;
  url: string;
}

/** Why a failed run stopped, as plain text. Mirrors the backend's `SpotterJobReport`. */
export interface ControlRoomJobReport {
  summary?: string;
  whatHappened?: string;
  whatToDo: string[];
  links: ControlRoomJobReportLink[];
  failedAt?: string;
  reference?: string;
}

/** A fix n8n offered for a failed run. Plain text; the API only sends it while it can still be
 *  approved. Mirrors the backend's `ControlRoomOffer`. */
export interface ControlRoomOffer {
  id: string;
  title?: string;
  description?: string;
  excluded: string[];
  effect?: string;
  expiresAt?: string;
  state: "available" | "approving";
}

export interface ControlRoomJob {
  jobId: string;
  mode: SpotterJobMode;
  /** `null` until the job's first main-stage push arrives — see `strings.ts`'s stage labels for
   *  how a `null` stage renders (falls back to the job's `latestStatus`/`needsInput` instead). */
  latestStage: SpotterJobMainStage | null;
  latestStatus: SpotterJobStatus;
  needsInput: boolean;
  note: string;
  /** Present while the job is in error (or done with a late error) and n8n has sent its report. */
  report?: ControlRoomJobReport;
  /** An approvable fix for the failure. Absent when there is none, or it expired or was used. */
  offer?: ControlRoomOffer;
  /** A done job that later reported an error. Shown as a warning; the job stays done. */
  hadLateError?: true;
  /** That late error's note. */
  lateErrorNote?: string;
  /** When the current error (or late error) was first pushed. */
  erroredAt?: string;
  createdAt: string;
  lastUpdatedAt: string;
  target?: ControlRoomJobTarget;
  /** Game/practice row label. Can arrive alongside `target` (collection/saved-search/assets runs
   *  also carry the game), so read it independently of `target`. */
  gameLabel?: ControlRoomGameLabel;
  /** Portal-only "get rid of it" flag (Monday 12921148830, Seth: jobs that hang forever and
   *  clutter the list) — set via `useSetControlRoomJobDismissed`, never by n8n. Always present
   *  (not optional like `target`), same as the backend's `ControlRoomJob.dismissed`. */
  dismissed: boolean;
  /** When the user last explicitly unhid this job, or `null` if never. Not read by the portal. */
  unhiddenAt: string | null;
  /** Whether this job has a Films Ground/Top Films anchor-job review to open — server-resolved
   *  (`apps/customer-api/src/services/spotterControlRoom.ts`'s join against `filmsGroundStore.ts`,
   *  Monday 12921515105). NOT derivable from `mode`: a `game`/`sideline`/etc. pipeline run can
   *  spawn a review (most commonly a slow-mo-band one) under the same `jobId`, so `mode` alone
   *  says nothing about whether one exists. `ControlRoomScreen.tsx` gates row clickability and
   *  "Open review" on this field, not on `mode`. */
  hasReview: boolean;
  /** `true` only while `hasReview` AND that anchor-job hasn't reached `"submitted"` yet — i.e.
   *  there's a real, outstanding customer action waiting on it (set the game start, or confirm
   *  anchors), same "not submitted yet" criterion `spotterJobStatusStore.ts`'s own
   *  `hasPendingAnchorReview` uses server-side. `false` once submitted, or when `hasReview` is
   *  `false`. `ControlRoomScreen.tsx`'s `primaryJobState` folds this into its `needsInput` bucket
   *  — deliberately ranked ABOVE `done`, unlike the plain `needsInput` latch above: a pipeline
   *  reaching `done` says nothing about whether this separate review track was ever resolved (the
   *  exact bug this field exists to fix), so it must not be able to hide an open review the way it
   *  can legitimately hide a stale/late pipeline `needsInput` push. */
  reviewNeedsInput: boolean;
  /** Monday 12991249614 — true iff this job HAD a review that a later duplicate create (same
   *  source/mode/band, a new jobId) collapsed — see the backend's own `ControlRoomJob.superseded`
   *  doc comment. Always `false` when `hasReview` is `true`, and always `false` when there was
   *  never a review here at all. `RailRowContent` uses this to explain an unclickable row instead
   *  of rendering it as a silent dead end. */
  superseded: boolean;
}

/** Re-polled on this interval — same 5s cadence `../gamebook/service.ts`'s
 *  `GAMEBOOK_POLL_INTERVAL_MS` already established for this app's own "watch a background job
 *  finish" pattern (itself borrowed from `apps/frontend/src/features/jobs/JobView.tsx`). Status
 *  pushes are fire-and-forget from n8n's side with no live-push mechanism to this app (see the
 *  backend route's doc comment) — polling is the only option, and a run here takes minutes, not
 *  seconds, so 5s is "real time" enough without hammering the API. Unconditional, not just while
 *  something is in progress: this screen shows every job across the whole org at once, so there's
 *  no single "done" moment to stop polling at — a customer leaving the tab open should keep
 *  seeing new jobs land and existing ones advance. */
export const CONTROL_ROOM_POLL_INTERVAL_MS = 5_000;

/** See the file doc comment's "FIXTURES" section — every fixture job is treated as a real,
 *  reviewable one (`hasReview: true`), `reviewNeedsInput` following the SAME "not submitted yet"
 *  rule the real backend uses. `lastUpdatedAt` is staggered a few minutes apart per job so
 *  `formatRelativeTime` shows something realistic. */
function buildFixtureControlRoomJobs(): ControlRoomJob[] {
  const now = Date.now();
  const reviewJobs = listFixtureJobSummaries().map(
    (summary, i): ControlRoomJob => {
      const submitted = summary.status === "submitted";
      return {
        jobId: summary.jobId,
        mode: summary.mode,
        latestStage: submitted
          ? "done"
          : summary.status === "needs_confirmation"
          ? "tagging"
          : "processing_footage",
        latestStatus: submitted ? "complete" : "in_progress",
        needsInput: false,
        note: "Fixture data",
        createdAt: new Date(now - (i + 1) * 60 * 60_000).toISOString(),
        lastUpdatedAt: new Date(now - i * 60_000).toISOString(),
        target: {
          kind: summary.source.kind,
          name: summary.source.name,
          url: summary.source.url,
          assetCount: summary.source.assetCount,
        },
        gameLabel: { primary: summary.gameLabel },
        dismissed: false,
        unhiddenAt: null,
        hasReview: true,
        reviewNeedsInput: !submitted,
        superseded: false,
      };
    }
  );
  return [...reviewJobs, ...buildFixtureFailedJobs(now)];
}

/** A failed job with a full report, one still waiting on it, a done job with a late error and a
 *  stalled one, so those states can be seen without n8n. */
function buildFixtureFailedJobs(now: number): ControlRoomJob[] {
  const base = {
    mode: "game" as const,
    latestStage: "processing_footage" as const,
    latestStatus: "error" as const,
    needsInput: false,
    createdAt: new Date(now - 2 * 60 * 60_000).toISOString(),
    dismissed: false,
    unhiddenAt: null,
    hasReview: false,
    reviewNeedsInput: false,
    superseded: false,
  };
  return [
    {
      ...base,
      jobId: "fixture_failed_report",
      note: "Run stopped",
      lastUpdatedAt: new Date(now - 30 * 60_000).toISOString(),
      target: { kind: "assets", assetCount: 12 },
      gameLabel: { primary: "2026 REG08 KC vs BUF", secondary: "Sideline" },
      report: {
        summary: "The footage file could not be read",
        whatHappened:
          "Spotter could not open the source video. The file looks incomplete or corrupt, so no plays were identified.",
        whatToDo: [
          "Re-export the file from your editing system.",
          "Upload it to the same collection and run Spotter again.",
        ],
        links: [{ label: "Contact support", url: "mailto:support@cinesys.io" }],
        failedAt: new Date(now - 30 * 60_000).toISOString(),
        reference: "fixture_failed_report-err1",
      },
    },
    {
      ...base,
      jobId: "fixture_failed_pending",
      note: "Reviewing failure and generating report",
      lastUpdatedAt: new Date(now - 60_000).toISOString(),
      erroredAt: new Date(now - 60_000).toISOString(),
      gameLabel: { primary: "2026 REG08 NYJ vs CLE" },
    },
    {
      ...base,
      jobId: "fixture_done_late_error",
      latestStage: "done",
      latestStatus: "complete",
      note: "Finished",
      hadLateError: true,
      lateErrorNote: "Export to iconik failed",
      lastUpdatedAt: new Date(now - 5 * 60_000).toISOString(),
      erroredAt: new Date(now - 5 * 60_000).toISOString(),
      gameLabel: { primary: "2026 REG08 DAL vs PHI" },
      report: {
        summary: "Plays were tagged but the export step failed",
        whatToDo: ["Run the export again from iconik."],
        links: [],
      },
    },
    {
      ...base,
      jobId: "fixture_stalled",
      latestStage: "identifying_plays",
      latestStatus: "in_progress",
      note: "Identifying plays",
      lastUpdatedAt: new Date(now - 7 * 60 * 60_000).toISOString(),
      gameLabel: { primary: "2026 REG08 SF vs SEA" },
    },
  ];
}

export async function getControlRoomJobs(): Promise<ControlRoomJob[]> {
  if (filmsGroundFixturesEnabled()) {
    return buildFixtureControlRoomJobs();
  }
  const { jobs } = await CustomerApiService.fetch<{ jobs: ControlRoomJob[] }>(
    "GET",
    "/spotter/control-room"
  );
  return jobs;
}

export const controlRoomQueryKeys = {
  jobs: ["spotter-control-room"] as const,
};

export function useControlRoomJobs(options?: {
  enabled?: boolean;
}): UseQueryResult<ControlRoomJob[], Error> {
  return useQuery<ControlRoomJob[], Error>(
    controlRoomQueryKeys.jobs,
    getControlRoomJobs,
    {
      refetchInterval: CONTROL_ROOM_POLL_INTERVAL_MS,
      enabled: options?.enabled,
    }
  );
}

/** Backs `SpotterHeader.tsx`'s Control Room tab badge (Addison: "show a little needs attention
 *  icon on the control room tab when you have stuff that needs you? Even when you're on a
 *  different tab?") — shares the SAME cache entry `useControlRoomJobs` reads (same query key, same
 *  poll interval), so mounting this on every Spotter screen's header doesn't add a second poll of
 *  the org's job list; react-query dedupes it to the one request already in flight for whichever
 *  screen is on Control Room itself. `tabForJob` (`jobBucketing.ts`) is the exact same predicate
 *  the rail's own "Needs you" section uses, so the badge can never disagree with what actually
 *  shows up there. Reads `false` (not the badge) while the query hasn't resolved yet or errored —
 *  a background chrome affordance shouldn't invent a spinner/error state of its own. `enabled`
 *  mirrors `SpotterHeader.tsx`'s own `access.status === "granted"` gate — no point polling this
 *  from a header rendered before entitlement is even confirmed. */
export function useControlRoomNeedsAttention(enabled: boolean): boolean {
  const jobsQuery = useControlRoomJobs({ enabled });
  const jobs = jobsQuery.data;
  return !!jobs && jobs.some((job) => tabForJob(job) === "needsYou");
}

/** Sets (or clears) one job's `dismissed` flag (Monday 12921148830) — `PUT
 *  /spotter/control-room/:jobId/dismiss`, mirrored server-side by
 *  `apps/customer-api/src/routes/spotter/controlRoom.ts`. */
async function setControlRoomJobDismissed(
  jobId: string,
  dismissed: boolean
): Promise<ControlRoomJob> {
  const { job } = await CustomerApiService.fetch<{ job: ControlRoomJob }>(
    "PUT",
    `/spotter/control-room/${encodeURIComponent(jobId)}/dismiss`,
    { dismissed }
  );
  return job;
}

export interface SetControlRoomJobDismissedVars {
  jobId: string;
  dismissed: boolean;
}

/** No optimistic update — this screen already polls every `CONTROL_ROOM_POLL_INTERVAL_MS`, so a
 *  plain invalidate-on-success (same convention `useGenerateGamebook` uses) picks up the change on
 *  the very next tick without extra bookkeeping. */
export function useSetControlRoomJobDismissed(): UseMutationResult<
  ControlRoomJob,
  Error,
  SetControlRoomJobDismissedVars
> {
  const queryClient = useQueryClient();
  return useMutation<ControlRoomJob, Error, SetControlRoomJobDismissedVars>(
    (vars) => setControlRoomJobDismissed(vars.jobId, vars.dismissed),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(controlRoomQueryKeys.jobs);
      },
    }
  );
}

/** `POST /spotter/control-room/:jobId/offer/approve`. `offerId` lets the server reject a click on
 *  an offer that has since been replaced. */
export async function approveControlRoomOffer(
  jobId: string,
  offerId: string
): Promise<ControlRoomJob> {
  const { job } = await CustomerApiService.fetch<{ job: ControlRoomJob }>(
    "POST",
    `/spotter/control-room/${encodeURIComponent(jobId)}/offer/approve`,
    { offer_id: offerId }
  );
  return job;
}
